import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackComponentParamsType,
  useTrackFeature,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  COLORS,
  HorizontalSliderProps,
  RowSection,
  SPACING,
  mediaDesktopAndUp,
  tabletAndUpLinaria,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback} from 'react';
import {ProductTileProps} from '../ProductTile/ProductTile';
import {ProductList, ProductTileConfig} from './ProductList';
import {TextBanner} from './shared';
import {useTrackRecoFeatureClick} from './useTrackRecoFeatureClick';

interface RecommendationsLaneProps {
  ['data-testid']?: string;
  className?: string;
  uniqueKey: string;
  streamPosition: number;
  type: 'grid' | 'list';
  headline?: string;
  topline?: string;
  products: ProductTileV2[];
  ProductTileComponent?: React.FC<ProductTileProps>;
  AdditionalTile?: React.ReactNode;
  onAddToBasketCtaClick?: ({
    productId,
    locationId,
    couponPromotionId,
  }: {
    productId: number;
    locationId?: string;
    couponPromotionId?: string;
  }) => void;
  promotionTracker?: PromotionTracker;
  trackComponentConfig: TrackComponentParamsType;
  productTileConfig?: ProductTileConfig;
  columns?: {small: number; medium: number; large: number};
  showDivider?: boolean;
  centered?: boolean;
  isSmallFormatHeader?: boolean;
  addToCTALabel?: string;
  /** Feature label for impression + specific click tracking (e.g., 'lastViewed', 'similarItems'). */
  featureTrackingLabel?: string;
  /** Generic feature click label fired on every product tile click (e.g., 'adpReco'). */
  featureGenericClickLabel?: string;
}

export const RecommendationsLane: React.FC<
  RecommendationsLaneProps & HorizontalSliderProps
> = ({
  'data-testid': dataTestId,
  className,
  uniqueKey,
  type,
  headline,
  topline,
  products,
  ProductTileComponent,
  AdditionalTile,
  onAddToBasketCtaClick,
  streamPosition,
  promotionTracker,
  trackComponentConfig,
  productTileConfig,
  columns,
  showDivider,
  centered,
  addToCTALabel,
  featureTrackingLabel,
  featureGenericClickLabel,
  ...sliderProps
}) => {
  const trackOriginClick = useTrackOriginClick();
  const trackFeature = useTrackFeature();

  const trackOrigin = () => {
    if (promotionTracker) {
      trackOriginClick(promotionTracker.locationId as originClick)();
    }
  };

  const {ref: featureImpressionRef} = useInViewEffect<HTMLElement>(
    useCallback(
      inView => {
        if (inView && featureTrackingLabel) {
          trackFeature({
            eventType: 'impression',
            label: featureTrackingLabel,
          });
        }
      },
      [featureTrackingLabel, trackFeature],
    ),
    {triggerOnce: true, threshold: 0.2},
  );

  const handleProductTileClick = useTrackRecoFeatureClick({
    featureLabel: featureTrackingLabel,
    genericLabel: featureGenericClickLabel,
  });

  const hasFeatureClickTracking = Boolean(
    featureGenericClickLabel || featureTrackingLabel,
  );

  const isGrid = type === 'grid';

  return (
    <RecoWrapper
      ref={featureTrackingLabel ? featureImpressionRef : undefined}
      className={className}
      data-testid={dataTestId}
      pullStart
      pullEnd
      $showDivider={showDivider}
      onClick={trackOrigin}
    >
      {isGrid && (headline || topline) && (
        <SliderTitleTextBanner
          headline={headline}
          subline={topline}
          centered={centered}
        />
      )}
      <ProductList
        testId={isGrid ? 'gridRecommendationLane' : 'listRecommendationLane'}
        uniqueKey={uniqueKey}
        streamPosition={streamPosition}
        type={type}
        products={products}
        ProductTileComponent={ProductTileComponent}
        AdditionalTile={AdditionalTile}
        onAddToBasketCtaClick={onAddToBasketCtaClick}
        promotionTracker={promotionTracker}
        trackComponentConfig={trackComponentConfig}
        productTileConfig={{
          ...productTileConfig,
          addCtaLabel: addToCTALabel,
        }}
        onProductTileClick={
          hasFeatureClickTracking ? handleProductTileClick : undefined
        }
        columns={columns}
        sliderProps={{
          ...sliderProps,
          title: !isGrid ? (
            <SliderTitleTextBanner
              headline={headline}
              subline={topline}
              centered={centered}
              isSmallFormat={sliderProps.isSmallFormatHeader}
              constrained={Boolean(sliderProps.constrained)}
            />
          ) : undefined,
        }}
      />
    </RecoWrapper>
  );
};

export const RecoWrapper = styled(RowSection)<{$showDivider?: boolean}>`
  position: relative;
  border-top: ${({$showDivider}) =>
    $showDivider ? `8px solid ${COLORS.Grey4}` : 'none'};

  ${tabletAndUpLinaria(`
    border-top: none;
  `)}
`;

const SliderTitleTextBanner = styled(TextBanner)`
  margin: ${SPACING['Y.Xl']};

  &[data-constrained='true'] {
    margin-inline: 0;
  }

  @media ${mediaDesktopAndUp} {
    margin: ${SPACING['Y.Xl']} ${SPACING['X.Xl6']};
  }
`;
